import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Plug, KeyRound, AlertTriangle, CheckCircle2, Scale, Download, RotateCcw, Copy, Wrench, ExternalLink } from 'lucide-react';
import { tallyConnectorApi, TallyConnectorStatus, TallyReconcileResult, VoucherCheckResult } from '../services/api';
import { useToast } from '../components/Toast/Toast';

/**
 * The ABS Tally Bridge, seen from the portal: is it running, what is waiting
 * to go to Tally, what Tally refused (and a Retry), the connector key, and a
 * reconciliation — vouchers and ledger balances, portal against Tally.
 */

const inr = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const ago = (s: number | null) => {
  if (s === null || s === undefined) return 'never';
  if (s < 90) return `${s}s ago`;
  if (s < 5400) return `${Math.round(s / 60)} min ago`;
  if (s < 172800) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} days ago`;
};
const firstOfMonth = () => {
  const d = new Date(Date.now() + 5.5 * 3600e3); d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 10);
};
const lastOfMonth = (from: string) => {
  const d = new Date(from + 'T00:00:00Z'); d.setUTCMonth(d.getUTCMonth() + 1); d.setUTCDate(0);
  return d.toISOString().slice(0, 10);
};

const TallyConnector: React.FC = () => {
  const { showError, showSuccess } = useToast();
  const [status, setStatus] = useState<TallyConnectorStatus | null>(null);
  const [errors, setErrors] = useState<any[]>([]);
  const [rec, setRec] = useState<TallyReconcileResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(lastOfMonth(firstOfMonth()));
  const [tab, setTab] = useState<'missing' | 'extra' | 'amount' | 'lines' | 'ledgers' | 'stock'>('missing');
  const [resending, setResending] = useState(false);
  // Marks the listed vouchers edited: on its next run the bridge alters each
  // Tally entry to match the portal. Compare again afterwards to confirm.
  const resend = async (ids: number[]) => {
    if (!window.confirm(`Send ${ids.length} voucher(s) to Tally again?\n\nThe bridge changes each Tally entry to match the portal on its next run.`)) return;
    setResending(true);
    try { const r = await tallyConnectorApi.resend(ids); showSuccess('Queued for Tally', `${r.data.resent} voucher(s) go on the bridge's next run — press Compare afterwards to check`); load(); }
    catch (e: any) { showError('Could not queue', e?.message); }
    finally { setResending(false); }
  };
  const [resyncFrom, setResyncFrom] = useState('2026-04-01');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, e, r] = await Promise.all([tallyConnectorApi.status(), tallyConnectorApi.errors(), tallyConnectorApi.reconcileResult()]);
      setStatus(s.data); setErrors(e.data || []); setRec(r.data || null);
    } catch (e: any) {
      showError('Could not load the Tally Connector', e?.message || 'Unknown error');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const createKey = async () => {
    if (status?.has_key && !window.confirm('Create a new key? The bridge stops working until you put the new key in its config.json.')) return;
    try { const r = await tallyConnectorApi.rotateKey(); setNewKey(r.data.key); load(); }
    catch (e: any) { showError('Could not create a key', e?.message); }
  };
  const retry = async (ids: number[]) => {
    try { await tallyConnectorApi.retry(ids); showSuccess(ids.length ? 'Will be sent again' : 'All will be sent again', 'On the bridge’s next run'); load(); }
    catch (e: any) { showError('Retry failed', e?.message); }
  };
  const askReconcile = async () => {
    try { await tallyConnectorApi.requestReconcile(from, to); showSuccess('Reconciliation requested', 'The bridge picks it up within two minutes'); load(); }
    catch (e: any) { showError('Could not request', e?.message); }
  };
  const fullResync = async () => {
    if (!window.confirm(`Full resync from ${resyncFrom}?

The bridge first brings every ledger and item in Tally in line with the portal — openings, opening bills and groups — then sends every voucher from ${resyncFrom}. Vouchers already in Tally are linked, not sent twice. Use it on a Tally company that should mirror the portal.`)) return;
    try { const r = await tallyConnectorApi.fullResync(resyncFrom); showSuccess('Full resync started', `${r.data.vouchers_to_send} vouchers to go, after the masters`); load(); }
    catch (e: any) { showError('Could not start', e?.message); }
  };
  const saveSyncFrom = async (d: string) => {
    try { await tallyConnectorApi.setSyncFrom(d); showSuccess('Saved', `Vouchers dated from ${d} go to Tally`); load(); }
    catch (e: any) { showError('Could not save', e?.message); }
  };

  const exportExcel = async () => {
    if (!rec) return;
    const { utils, writeFile } = await import('xlsx');
    const wb = utils.book_new();
    utils.book_append_sheet(wb, utils.json_to_sheet(rec.per_type), 'Summary');
    utils.book_append_sheet(wb, utils.json_to_sheet(rec.missing_in_tally), 'Missing in Tally');
    utils.book_append_sheet(wb, utils.json_to_sheet(rec.not_in_portal), 'Not in portal');
    utils.book_append_sheet(wb, utils.json_to_sheet(rec.amount_differs), 'Amount differs');
    if (rec.lines_differ) utils.book_append_sheet(wb, utils.json_to_sheet(rec.lines_differ), 'Ledgers-items differ');
    utils.book_append_sheet(wb, utils.json_to_sheet(rec.ledger_differs), 'Ledger balances');
    if (rec.stock_differs) utils.book_append_sheet(wb, utils.json_to_sheet(rec.stock_differs), 'Stock');
    writeFile(wb, `Tally_Reconciliation_${rec.from}_to_${rec.to}.xlsx`);
  };

  const s = status;
  const reconcileWaiting = !!s?.reconcile.requested_at && (!s.reconcile.done_at || new Date(s.reconcile.requested_at) > new Date(s.reconcile.done_at));

  return (
    <div className="min-h-screen bg-gray-100 p-3">
      <div className="bg-white rounded-lg shadow p-4 max-w-[1300px] mx-auto space-y-4">
        <div className="flex items-center gap-2">
          <Plug className="h-5 w-5 text-blue-600" />
          <h1 className="text-lg font-semibold text-gray-800 flex-1">Tally Connector</h1>
          <button onClick={load} disabled={loading} className="p-2 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50" title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {s && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className={`rounded-lg border p-3 ${s.online ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <div className="text-[11px] font-bold uppercase text-gray-500">Bridge</div>
              <div className={`text-xl font-bold ${s.online ? 'text-emerald-700' : 'text-red-700'}`}>{s.online ? 'Online' : 'Offline'}</div>
              <div className="text-[11px] text-gray-500">last heard {ago(s.seen_seconds_ago)}{s.info?.pc ? ` · ${s.info.pc}` : ''}</div>
            </div>
            <div className={`rounded-lg border p-3 ${s.info?.tally?.company_open ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="text-[11px] font-bold uppercase text-gray-500">Tally</div>
              <div className="text-xl font-bold text-gray-800">{!s.info ? '—' : s.info.tally?.company_open ? 'Company open' : s.info.tally?.reachable ? 'Company closed' : 'Not running'}</div>
              <div className="text-[11px] text-gray-500 truncate">{s.info?.company || ''}</div>
            </div>
            <div className="rounded-lg border p-3 bg-gray-50 border-gray-200">
              <div className="text-[11px] font-bold uppercase text-gray-500">Waiting to go</div>
              <div className="text-xl font-bold text-gray-800">{s.pending}</div>
              <div className="text-[11px] text-gray-500">new or edited, from {s.sync_from || '—'}</div>
            </div>
            <div className={`rounded-lg border p-3 ${s.errors ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="text-[11px] font-bold uppercase text-gray-500">Refused by Tally</div>
              <div className={`text-xl font-bold ${s.errors ? 'text-red-700' : 'text-gray-800'}`}>{s.errors}</div>
              <div className="text-[11px] text-gray-500">{s.deletions_pending ? `${s.deletions_pending} deletion(s) waiting` : 'see the list below'}</div>
            </div>
            <div className="rounded-lg border p-3 bg-gray-50 border-gray-200">
              <div className="text-[11px] font-bold uppercase text-gray-500">Sent so far</div>
              <div className="text-xl font-bold text-gray-800">{s.info?.totals ? (s.info.totals.sent + s.info.totals.linked) : '—'}</div>
              <div className="text-[11px] text-gray-500">
                {s.info?.last_cycle?.ok === false ? <span className="text-red-600">last run failed: {String(s.info.last_cycle.error).slice(0, 60)}</span>
                  : s.info?.totals ? `since bridge start · ${s.info.totals.replaced} replaced, ${s.info.totals.deleted} deleted` : ''}
              </div>
            </div>
          </div>
        )}

        {/* Setup */}
        <div className="border border-gray-200 rounded p-3 flex flex-wrap items-center gap-3 text-sm">
          <KeyRound className="h-4 w-4 text-gray-500" />
          <span className="text-gray-700">
            Connector key: {s?.has_key ? <>ending <strong>…{s.key_hint}</strong>, made {String(s.key_created_at || '').slice(0, 16)} by {s.key_created_by}</> : <strong className="text-red-600">none yet</strong>}
          </span>
          <button onClick={createKey} className="px-3 py-1.5 text-xs rounded border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100">
            {s?.has_key ? 'Create new key' : 'Create key'}
          </button>
          <span className="flex-1" />
          <label className="text-gray-600 text-xs">Send vouchers dated from</label>
          <input type="date" defaultValue={s?.sync_from || ''} key={s?.sync_from || 'x'} onBlur={e => e.target.value && e.target.value !== s?.sync_from && saveSyncFrom(e.target.value)} className="px-2 py-1 text-sm border border-gray-300 rounded" />
        </div>
        {/* Full resync: masters with openings, then every voucher from a date */}
        <div className="border border-gray-200 rounded p-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <RotateCcw className="h-4 w-4 text-gray-500" />
            <span className="font-semibold text-gray-700">Full resync</span>
            <span className="text-xs text-gray-500 flex-1">Ledger and item masters with their openings, then every voucher from the date, sent to Tally again. One already in Tally is linked, not sent twice.</span>
            <input type="date" value={resyncFrom} onChange={e => setResyncFrom(e.target.value)} className="px-2 py-1 text-sm border border-gray-300 rounded" />
            <button onClick={fullResync} className="px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700">Start full resync</button>
          </div>
          {s?.masters?.requested_at && (
            <div className="mt-2 text-xs text-gray-600">
              {!s.masters.done_at || new Date(s.masters.requested_at) > new Date(s.masters.done_at)
                ? <span className="text-amber-700">Requested {String(s.masters.requested_at).slice(0, 16)} — the bridge does the masters on its next run, then the vouchers.</span>
                : s.masters.result && (
                  <>
                    <span className="text-emerald-700 font-semibold">Masters done {String(s.masters.done_at).slice(0, 16)}:</span>{' '}
                    groups {s.masters.result.groups_created}, ledgers created {s.masters.result.ledgers_created} / updated {s.masters.result.ledgers_altered},
                    items created {s.masters.result.items_created} / updated {s.masters.result.items_altered}
                    {s.masters.result.failed?.length ? <span className="text-red-600">, refused {s.masters.result.failed.length}</span> : null}.
                    {' '}Openings Dr ₹{inr(s.masters.result.opening_dr)} · Cr ₹{inr(s.masters.result.opening_cr)}
                    {Math.abs(s.masters.result.opening_difference || 0) >= 0.01 && <span className="text-red-600"> · difference ₹{inr(s.masters.result.opening_difference)} (shows in Tally as "Difference in opening balances")</span>}
                    {(s.masters.result.failed?.length || s.masters.result.notes?.length) ? (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-blue-600">Refused and adjusted ({(s.masters.result.failed?.length || 0) + (s.masters.result.notes?.length || 0)})</summary>
                        <ul className="mt-1 max-h-60 overflow-auto list-disc pl-5">
                          {(s.masters.result.failed || []).map((f: string, i: number) => <li key={'f' + i} className="text-red-700">{f}</li>)}
                          {(s.masters.result.notes || []).map((n: string, i: number) => <li key={'n' + i}>{n}</li>)}
                        </ul>
                      </details>
                    ) : null}
                  </>
                )}
            </div>
          )}
        </div>
        {newKey && (
          <div className="border border-amber-300 bg-amber-50 rounded p-3 text-sm">
            <div className="font-semibold text-amber-900 mb-1">Copy this key now — it is shown only once.</div>
            <div className="flex items-center gap-2">
              <code className="px-2 py-1 bg-white border rounded font-mono text-xs break-all flex-1">{newKey}</code>
              <button onClick={() => { navigator.clipboard?.writeText(newKey); showSuccess('Copied', ''); }} className="p-2 rounded border border-gray-300 bg-white" title="Copy"><Copy className="h-4 w-4" /></button>
            </div>
            <div className="text-xs text-amber-800 mt-1">Put it in <code>tally-bridge\config.json</code> as <code>connectorKey</code> on the Tally PC.</div>
          </div>
        )}

        {/* Errors */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <span className="text-[11px] font-bold uppercase text-gray-500 flex-1">Refused by Tally ({errors.length})</span>
            {errors.length > 0 && <button onClick={() => retry([])} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50"><RotateCcw className="h-3.5 w-3.5" /> Retry all</button>}
          </div>
          {errors.length === 0 ? (
            <div className="text-sm text-gray-400 border border-dashed rounded p-3 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Nothing refused.</div>
          ) : (
            <div className="overflow-auto max-h-80 border border-gray-200 rounded">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0">
                  <tr><th className="px-2 py-1.5 text-left">Date</th><th className="px-2 py-1.5 text-left">Type</th><th className="px-2 py-1.5 text-left">No.</th><th className="px-2 py-1.5 text-left">Party</th><th className="px-2 py-1.5 text-right">Amount</th><th className="px-2 py-1.5 text-left">Tally said</th><th className="px-2 py-1.5 text-right">Tries</th><th className="px-2 py-1.5"></th></tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {errors.map(e => (
                    <tr key={e.vch_id}>
                      <td className="px-2 py-1.5 whitespace-nowrap">{String(e.vch_date || '').slice(0, 10)}</td>
                      <td className="px-2 py-1.5">{e.vch_type}</td>
                      <td className="px-2 py-1.5 font-semibold">{e.vch_no}</td>
                      <td className="px-2 py-1.5">{e.party_name}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{inr(e.amount)}</td>
                      <td className="px-2 py-1.5 text-red-700 max-w-[420px]">{e.message}</td>
                      <td className="px-2 py-1.5 text-right">{e.attempts}</td>
                      <td className="px-2 py-1.5 text-right"><button onClick={() => retry([e.vch_id])} className="text-blue-600 hover:underline">Retry</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[11px] text-gray-400 mt-1">A refused voucher is sent again automatically once it is edited, or straight away with Retry.</p>
        </div>

        <VoucherCheckPanel />

        {/* Reconciliation */}
        <div className="border-t pt-3">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Scale className="h-4 w-4 text-blue-600" />
            <span className="text-[11px] font-bold uppercase text-gray-500">Portal vs Tally</span>
            <span className="flex-1" />
            <input type="date" value={from} onChange={e => { setFrom(e.target.value); if (e.target.value) setTo(lastOfMonth(e.target.value)); }} className="px-2 py-1 text-sm border border-gray-300 rounded" />
            <span className="text-gray-400 text-sm">to</span>
            <input type="date" value={to} min={from} onChange={e => setTo(e.target.value)} className="px-2 py-1 text-sm border border-gray-300 rounded" />
            <button onClick={askReconcile} disabled={reconcileWaiting} className="px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
              {reconcileWaiting ? 'Waiting for the bridge…' : 'Compare'}
            </button>
            {rec && <button onClick={exportExcel} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-50"><Download className="h-3.5 w-3.5" /> Excel</button>}
          </div>

          {!rec ? (
            <div className="text-sm text-gray-400 border border-dashed rounded p-3">No comparison yet. Pick a period and press Compare; the bridge reads Tally and sends it here.</div>
          ) : (
            <>
              <div className="text-xs text-gray-500 mb-2">
                {rec.from} to {rec.to}{rec.company ? ` · ${rec.company}` : ''} · compared {String(rec.done_at || '').slice(0, 16)}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-3">
                {[
                  { l: 'Vouchers matched', v: `${rec.vouchers.matched} / ${rec.vouchers.portal}`, ok: rec.vouchers.matched === rec.vouchers.portal },
                  { l: 'Missing in Tally', v: rec.vouchers.missing_in_tally, ok: !rec.vouchers.missing_in_tally },
                  { l: 'In Tally, not portal', v: rec.vouchers.not_in_portal, ok: !rec.vouchers.not_in_portal },
                  { l: 'Amount differs', v: rec.vouchers.amount_differs, ok: !rec.vouchers.amount_differs },
                  { l: 'Lines differ', v: rec.vouchers.lines_differ ?? '—', ok: !rec.vouchers.lines_differ },
                  { l: 'Ledgers matched', v: rec.ledgers.skipped ? 'not compared' : `${rec.ledgers.matched}`, ok: true },
                  { l: 'Balances differ', v: rec.ledgers.skipped ? '—' : rec.ledgers.differ, ok: rec.ledgers.skipped || !rec.ledgers.differ },
                ].map(c => (
                  <div key={c.l} className={`rounded-lg border p-3 ${c.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="text-[11px] font-bold uppercase text-gray-500">{c.l}</div>
                    <div className={`text-xl font-bold ${c.ok ? 'text-emerald-700' : 'text-red-700'}`}>{c.v}</div>
                  </div>
                ))}
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded mb-3">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
                    <tr><th className="px-2 py-1.5 text-left">Type</th><th className="px-2 py-1.5 text-right">Portal no.</th><th className="px-2 py-1.5 text-right">Tally no.</th><th className="px-2 py-1.5 text-right">Portal amount</th><th className="px-2 py-1.5 text-right">Tally amount</th><th className="px-2 py-1.5 text-right">Difference</th></tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rec.per_type.map(r => {
                      const diff = Math.round((r.portal_amount - r.tally_amount) * 100) / 100;
                      const bad = r.portal_count !== r.tally_count || Math.abs(diff) >= 1;
                      return (
                        <tr key={r.type} className={bad ? 'bg-red-50/60' : ''}>
                          <td className="px-2 py-1.5 font-semibold">{r.type}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{r.portal_count}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{r.tally_count}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{inr(r.portal_amount)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{inr(r.tally_amount)}</td>
                          <td className={`px-2 py-1.5 text-right tabular-nums ${bad ? 'text-red-700 font-bold' : 'text-gray-400'}`}>{Math.abs(diff) >= 1 ? inr(diff) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap gap-1 mb-2">
                {([
                  ['missing', `Missing in Tally (${rec.missing_in_tally.length})`],
                  ['extra', `In Tally, not portal (${rec.not_in_portal.length})`],
                  ['amount', `Amount differs (${rec.amount_differs.length})`],
                  ['lines', `Ledgers / items / amounts differ (${rec.lines_differ?.length ?? 0})`],
                  ['ledgers', `Ledger balances (${rec.ledger_differs.length})`],
                  ['stock', `Stock (${rec.stock_differs?.length ?? 0} of ${rec.stock?.compared ?? 0} differ)`],
                ] as const).map(([k, l]) => (
                  <button key={k} onClick={() => setTab(k)} className={`px-2.5 py-1 text-xs rounded border ${tab === k ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>{l}</button>
                ))}
                {(() => {
                  // Vouchers on the open list that the portal can send again:
                  // the bridge alters each Tally entry to match the portal.
                  const list: any[] = tab === 'amount' ? rec.amount_differs : tab === 'lines' ? (rec.lines_differ || []) : tab === 'missing' ? rec.missing_in_tally : [];
                  const ids = list.map(v => Number(v.id)).filter(n => n > 0);
                  if (!ids.length) return null;
                  return (
                    <button onClick={() => resend(ids)} disabled={resending} className="ml-auto px-2.5 py-1 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                      {resending ? 'Sending…' : `Send these ${ids.length} again to Tally`}
                    </button>
                  );
                })()}
              </div>
              <div className="overflow-auto max-h-[420px] border border-gray-200 rounded">
                <table className="w-full text-xs">
                  {tab === 'stock' ? (
                    <>
                      <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0"><tr><th className="px-2 py-1.5 text-left">Item</th><th className="px-2 py-1.5 text-right">Portal closing qty</th><th className="px-2 py-1.5 text-right">Tally closing qty</th><th className="px-2 py-1.5 text-right">Difference</th></tr></thead>
                      <tbody className="divide-y divide-gray-100">
                        {(rec.stock_differs || []).map((x, i) => (
                          <tr key={i}><td className="px-2 py-1.5 font-semibold">{x.name}</td><td className="px-2 py-1.5 text-right tabular-nums">{x.portal_qty ?? 'not in portal'}</td><td className="px-2 py-1.5 text-right tabular-nums">{x.tally_qty ?? 'not in Tally'}</td><td className="px-2 py-1.5 text-right tabular-nums text-red-700 font-semibold">{x.difference}</td></tr>
                        ))}
                      </tbody>
                    </>
                  ) : tab === 'ledgers' ? (
                    <>
                      <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0"><tr><th className="px-2 py-1.5 text-left">Ledger</th><th className="px-2 py-1.5 text-left">Group</th><th className="px-2 py-1.5 text-right">Portal (Dr +)</th><th className="px-2 py-1.5 text-right">Tally (Dr +)</th><th className="px-2 py-1.5 text-right">Difference</th></tr></thead>
                      <tbody className="divide-y divide-gray-100">
                        {rec.ledger_differs.map((l, i) => (
                          <tr key={i}><td className="px-2 py-1.5 font-semibold">{l.name}</td><td className="px-2 py-1.5 text-gray-500">{l.group || ''}</td><td className="px-2 py-1.5 text-right tabular-nums">{l.portal_balance === null ? 'not in portal' : inr(l.portal_balance)}</td><td className="px-2 py-1.5 text-right tabular-nums">{l.tally_balance === null ? 'not in Tally' : inr(l.tally_balance)}</td><td className="px-2 py-1.5 text-right tabular-nums text-red-700 font-semibold">{inr(l.difference)}</td></tr>
                        ))}
                      </tbody>
                    </>
                  ) : tab === 'lines' ? (
                    <>
                      <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0"><tr><th className="px-2 py-1.5 text-left">Date</th><th className="px-2 py-1.5 text-left">Type</th><th className="px-2 py-1.5 text-left">No.</th><th className="px-2 py-1.5 text-left">Party</th><th className="px-2 py-1.5 text-right">Amount</th><th className="px-2 py-1.5 text-left">Only in portal</th><th className="px-2 py-1.5 text-left">Only in Tally</th><th className="px-2 py-1.5 text-left">Amount differs</th></tr></thead>
                      <tbody className="divide-y divide-gray-100">
                        {(rec.lines_differ || []).map((v: any, i: number) => (
                          <tr key={i}><td className="px-2 py-1.5 whitespace-nowrap">{v.date}</td><td className="px-2 py-1.5">{v.type}</td><td className="px-2 py-1.5 font-semibold">{v.no}{v.tally_no && v.tally_no !== v.no ? <span className="text-gray-400"> (Tally {v.tally_no})</span> : null}</td><td className="px-2 py-1.5">{v.party}</td><td className="px-2 py-1.5 text-right tabular-nums">{inr(v.portal_amount)}</td><td className="px-2 py-1.5 text-emerald-700">{v.only_in_portal || '—'}</td><td className="px-2 py-1.5 text-red-700">{v.only_in_tally || '—'}</td><td className="px-2 py-1.5 text-amber-700">{v.amounts_differ || '—'}</td></tr>
                        ))}
                      </tbody>
                    </>
                  ) : (
                    <>
                      <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0"><tr><th className="px-2 py-1.5 text-left">Date</th><th className="px-2 py-1.5 text-left">Type</th><th className="px-2 py-1.5 text-left">No.</th><th className="px-2 py-1.5 text-left">Party</th><th className="px-2 py-1.5 text-right">Portal</th><th className="px-2 py-1.5 text-right">Tally</th></tr></thead>
                      <tbody className="divide-y divide-gray-100">
                        {(tab === 'missing' ? rec.missing_in_tally : tab === 'extra' ? rec.not_in_portal : rec.amount_differs).map((v: any, i: number) => (
                          <tr key={i}><td className="px-2 py-1.5 whitespace-nowrap">{v.date}</td><td className="px-2 py-1.5">{v.type}</td><td className="px-2 py-1.5 font-semibold">{v.no}{v.tally_no && v.tally_no !== v.no ? <span className="text-gray-400"> (Tally {v.tally_no})</span> : null}</td><td className="px-2 py-1.5">{v.party}</td><td className="px-2 py-1.5 text-right tabular-nums">{inr(v.portal_amount)}</td><td className="px-2 py-1.5 text-right tabular-nums">{inr(v.tally_amount)}</td></tr>
                        ))}
                      </tbody>
                    </>
                  )}
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Voucher Check: vouchers whose own figures do not add up (debit ≠ credit,
 * batches ≠ their line, a total that is a paisa off…). Safe fixes run with
 * one click and are recorded; a fixed voucher counts as edited, so the Tally
 * Bridge sends the corrected one. The rest need a person: open the voucher.
 */
const CODE_LABEL: Record<string, string> = {
  DRCR: 'Debit ≠ credit', GSTTYPE: 'CGST/SGST vs IGST', TOTAL: 'Total', GOODS: 'Items ≠ line', BATCH: 'Batches', RATE: 'Qty × rate', GST: 'GST', LEDGER: 'Missing ledger',
};
const VoucherCheckPanel: React.FC = () => {
  const { showError, showSuccess } = useToast();
  const [vfrom, setVFrom] = useState('2026-04-01');
  const [vto, setVTo] = useState(new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10));
  const [res, setRes] = useState<VoucherCheckResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [fixing, setFixing] = useState<number | 'all' | null>(null);
  const [only, setOnly] = useState<string>('');

  const run = async () => {
    setBusy(true);
    try { setRes((await tallyConnectorApi.voucherChecks(vfrom, vto)).data); }
    catch (e: any) { showError('Check failed', e?.message); }
    finally { setBusy(false); }
  };
  const fixOne = async (id: number) => {
    setFixing(id);
    try {
      const r = (await tallyConnectorApi.voucherFix(id)).data;
      showSuccess(r.fixed.length ? 'Corrected' : 'Nothing to correct', r.fixed.join(', ') || (r.remaining.length ? 'What is left needs a person' : ''));
      await run();
    } catch (e: any) { showError('Could not correct', e?.message); }
    finally { setFixing(null); }
  };
  const fixAll = async () => {
    const n = res?.vouchers.filter(v => v.problems.some(p => p.fixable)).length || 0;
    if (!window.confirm(`Correct ${n} voucher(s) with safe fixes? Each change is recorded, and the corrected vouchers go to Tally again.`)) return;
    setFixing('all');
    try { const r = (await tallyConnectorApi.voucherFixAll(vfrom, vto)).data; showSuccess('Corrected', `${r.fixed_vouchers} voucher(s)`); await run(); }
    catch (e: any) { showError('Could not correct', e?.message); }
    finally { setFixing(null); }
  };

  const list = (res?.vouchers || []).filter(v => !only || v.problems.some(p => p.code === only));
  const fixableCount = (res?.vouchers || []).filter(v => v.problems.some(p => p.fixable)).length;

  return (
    <div className="border-t pt-3">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Wrench className="h-4 w-4 text-blue-600" />
        <span className="text-[11px] font-bold uppercase text-gray-500">Voucher check — figures that do not add up</span>
        <span className="flex-1" />
        <input type="date" value={vfrom} onChange={e => setVFrom(e.target.value)} className="px-2 py-1 text-sm border border-gray-300 rounded" />
        <span className="text-gray-400 text-sm">to</span>
        <input type="date" value={vto} min={vfrom} onChange={e => setVTo(e.target.value)} className="px-2 py-1 text-sm border border-gray-300 rounded" />
        <button onClick={run} disabled={busy} className="px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">{busy ? 'Checking…' : 'Check'}</button>
        {fixableCount > 0 && (
          <button onClick={fixAll} disabled={fixing !== null} className="px-3 py-1.5 text-xs rounded border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 disabled:opacity-50">
            {fixing === 'all' ? 'Correcting…' : `Correct all safe (${fixableCount})`}
          </button>
        )}
      </div>
      {!res ? (
        <div className="text-sm text-gray-400 border border-dashed rounded p-3">Pick a period and press Check. Nothing is changed until you press Correct.</div>
      ) : res.with_problems === 0 ? (
        <div className="text-sm text-gray-500 border border-dashed rounded p-3 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> All {res.checked} vouchers add up.</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1 mb-2 text-xs">
            <span className="text-gray-500 mr-1 self-center">{res.with_problems} of {res.checked} vouchers:</span>
            <button onClick={() => setOnly('')} className={`px-2 py-0.5 rounded border ${!only ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300'}`}>All</button>
            {Object.entries(res.counts).map(([k, n]) => (
              <button key={k} onClick={() => setOnly(k)} className={`px-2 py-0.5 rounded border ${only === k ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300'}`}>{CODE_LABEL[k] || k} ({n})</button>
            ))}
          </div>
          <div className="overflow-auto max-h-[420px] border border-gray-200 rounded">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0">
                <tr><th className="px-2 py-1.5 text-left">Date</th><th className="px-2 py-1.5 text-left">Type</th><th className="px-2 py-1.5 text-left">No.</th><th className="px-2 py-1.5 text-left">Party</th><th className="px-2 py-1.5 text-left">What does not add up</th><th className="px-2 py-1.5"></th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {list.map(v => (
                  <tr key={v.id} className="align-top">
                    <td className="px-2 py-1.5 whitespace-nowrap">{v.date}</td>
                    <td className="px-2 py-1.5">{v.type}</td>
                    <td className="px-2 py-1.5 font-semibold whitespace-nowrap">{v.vch_no}</td>
                    <td className="px-2 py-1.5">{v.party}</td>
                    <td className="px-2 py-1.5">
                      {v.problems.map((p, i) => (
                        <div key={i} className={p.fixable ? 'text-amber-800' : 'text-red-700'}>
                          <span className="font-semibold">{CODE_LABEL[p.code] || p.code}:</span> {p.message}{p.fixable ? '' : ' (needs a person)'}
                        </div>
                      ))}
                      {v.checked_by && <div className="text-gray-400">Marked checked by {v.checked_by}</div>}
                    </td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      {v.problems.some(p => p.fixable) && (
                        <button onClick={() => fixOne(v.id)} disabled={fixing !== null} className="text-emerald-700 hover:underline mr-2 disabled:opacity-50">{fixing === v.id ? '…' : 'Correct'}</button>
                      )}
                      <a href={`/billing/vouchers/edit/${v.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-blue-600 hover:underline">Open <ExternalLink className="h-3 w-3" /></a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-gray-400 mt-1">
            Correct changes only what has one right answer: a paisa-off total, batches made to match their line, a debit/credit
            difference up to ₹1 posted to Roundoff, a rate worked out from amount ÷ qty. Each change is recorded and the voucher
            goes to Tally again. Red ones need a person — open the voucher.
          </p>
        </>
      )}
    </div>
  );
};

export default TallyConnector;
